import { ObservableInputsManager } from '@/shared/utils/angular-sdk';
import { UiFormFieldComponent } from '@/ui/form-field/form-field/ui-form-field';
import { UiSelectComponent } from '@/ui/select/ui-select';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, type OnChanges, type OnInit, Output, type SimpleChanges, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, type UntypedFormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslatePipe } from '@ngx-translate/core';
import { AdvizeoInputDirective } from '../../../../directives/advizeo-input/advizeo-input.directive';
import { BusinessIntelligenceRepresentationConfigEditorComponent } from '../business-intelligence-representation-config-editor.class';
import type { BusinessIntelligenceDimension, BusinessIntelligenceMeasure } from './../../../../interfaces/BusinessIntelligence.interface';
import type { BusinessIntelligenceSingleValueRepresentationConfig } from './../../../../interfaces/BusinessIntelligenceRepresentation.interface';

@UntilDestroy()
@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./business-intelligence-single-value-representation-config-editor.scss'],
    selector: 'business-intelligence-single-value-representation-config-editor',
    templateUrl: 'business-intelligence-single-value-representation-config-editor.html',
    imports: [FormsModule, ReactiveFormsModule, UiFormFieldComponent, UiSelectComponent, AdvizeoInputDirective, TranslatePipe]
})
export class BusinessIntelligenceSingleValueRepresentationConfigEditorComponent
    extends BusinessIntelligenceRepresentationConfigEditorComponent<BusinessIntelligenceSingleValueRepresentationConfig>
    implements OnInit, OnChanges
{
    private _fb = inject(UntypedFormBuilder);

    private _observableInputsManager = new ObservableInputsManager<BusinessIntelligenceSingleValueRepresentationConfigEditorComponent>(
        this
    );

    @Input() config!: BusinessIntelligenceSingleValueRepresentationConfig;
    config$ = this._observableInputsManager.observe('config');

    @Input() availableMeasures: BusinessIntelligenceMeasure[] = [];
    availableMeasures$ = this._observableInputsManager.observe('availableMeasures');

    @Input() availableDimensions: BusinessIntelligenceDimension[] = [];
    availableDimensions$ = this._observableInputsManager.observe('availableDimensions');

    @Input() disabled!: boolean;

    @Output() configChanged: EventEmitter<BusinessIntelligenceSingleValueRepresentationConfig> = new EventEmitter();

    formGroup!: UntypedFormGroup;

    ngOnInit() {
        this.formGroup = this._fb.group({
            measureCode: [null, Validators.required],
            caption: [null]
        });
        this.config$.pipe(untilDestroyed(this)).subscribe(config => {
            if (!config) {
                this.formGroup.setValue(
                    {
                        measureCode: null,
                        caption: null
                    },
                    { emitEvent: false }
                );
            } else {
                this.formGroup.setValue(
                    {
                        measureCode: config.measure ? config.measure.measureCode : null,
                        caption: config.caption ? config.caption : null
                    },
                    { emitEvent: false }
                );
            }
        });
        this.formGroup.valueChanges.pipe(untilDestroyed(this)).subscribe(formValue => {
            this.configChanged.emit({
                measure: {
                    measureCode: formValue.measureCode
                },
                caption: formValue.caption
            });
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        this._observableInputsManager.triggerChanges(changes);
    }
}
